"use client";

import { Check, Copy, Mail, Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { copyWithFallback, shareOrFallback } from "@/lib/editorial/share";

type EditorialShareButtonProps = { url: string; title: string };

export function EditorialShareButton({ url, title }: EditorialShareButtonProps) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const onPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    copyRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  async function copyLink() {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const succeeded = await copyWithFallback({
      writeClipboard: navigator.clipboard?.writeText ? () => navigator.clipboard.writeText(url) : undefined,
      legacyCopy: () => {
      const textarea = document.createElement("textarea");
      try {
        textarea.value = url;
        textarea.style.cssText = "position:fixed;opacity:0";
        document.body.appendChild(textarea);
        textarea.select();
        return document.execCommand("copy");
      } catch { return false; } finally {
        textarea.remove();
        previouslyFocused?.focus();
      }
      },
    });
    if (succeeded) {
      setCopyError(false);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } else {
      setCopyError(true);
    }
  }

  async function share() {
    await shareOrFallback({
      share: navigator.share?.bind(navigator), title, url,
      openFallback: () => setOpen(true),
    });
  }

  return (
    <div className="relative" ref={menuRef} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Share this article"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={share}
        className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-800 transition hover:border-emerald-300 hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700"
      >
        <Share2 size={16} aria-hidden="true" /> Share
      </button>
      {open && (
        <div role="dialog" aria-label="Share options" className="absolute right-0 z-20 mt-2 grid min-w-40 gap-1 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
          <button ref={copyRef} type="button" onClick={copyLink} className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700">
            {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
            {copied ? "Copied" : "Copy link"}
          </button>
          <a href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`} className="flex min-h-11 items-center gap-2 rounded-lg px-3 text-sm font-semibold hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700">
            <Mail size={16} aria-hidden="true" /> Email
          </a>
          {copyError && <p className="max-w-72 px-3 py-2 text-xs leading-5 text-slate-600" role="status">Could not copy automatically. Select this article link: <span className="block select-all break-all">{url}</span></p>}
        </div>
      )}
      <span className="sr-only" aria-live="polite">{copied ? "Article link copied" : ""}</span>
    </div>
  );
}
